import { create } from 'zustand';
import { type ApiClient } from '../api/client';
import { apiClient } from '../api/default-client';
import { getRuntimeConfig } from '../config/runtime-config';

export const FEATURE_PREFIX = 'feature.';
export const EFFECTIVE_CONFIGURATION_PATH = '/api/v1/platform/configuration/effective';

/** One resolved configuration value with its provenance (10-configuration-inheritance-model.md). */
export interface EffectiveConfigurationValue {
  key: string;
  value: unknown;
  scope: string;
  scopeId?: string;
  provenance?: Array<{ scope: string; scopeId?: string; value: unknown }>;
}

export interface FeatureFlagsState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Server-resolved flags keyed by full key (`feature.yard`). */
  flags: Record<string, boolean>;
  /** Local overrides (runtime config `features` + `setOverride`), win over server flags. */
  overrides: Record<string, boolean>;
  /** `GET /api/v1/platform/configuration/effective?key=feature.*` */
  load(client?: ApiClient): Promise<void>;
  setOverride(key: string, value: boolean | null): void;
  isEnabled(key: string): boolean;
}

/** Normalises `yard` / `feature.yard` to `feature.yard`. */
export function featureKey(key: string): string {
  return key.startsWith(FEATURE_PREFIX) ? key : `${FEATURE_PREFIX}${key}`;
}

/** Coerces configuration values (`true`, `"true"`, `1`, `"on"`, `"enabled"`) to booleans. */
export function toFlag(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'on' || v === 'yes' || v === 'enabled';
  }
  return false;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Accepts an array, a `Page` (`content[]`), an `items[]` wrapper or a plain key→value map. */
export function parseEffectiveFlags(body: unknown): Record<string, boolean> {
  const flags: Record<string, boolean> = {};
  let entries: unknown[] = [];
  if (Array.isArray(body)) entries = body;
  else if (isRecord(body) && Array.isArray(body.content)) entries = body.content;
  else if (isRecord(body) && Array.isArray(body.items)) entries = body.items;
  else if (isRecord(body) && Array.isArray(body.values)) entries = body.values;
  else if (isRecord(body)) {
    for (const [k, v] of Object.entries(body)) if (k.startsWith(FEATURE_PREFIX)) flags[k] = toFlag(v);
    return flags;
  }
  for (const entry of entries) {
    if (!isRecord(entry) || typeof entry.key !== 'string') continue;
    if (!entry.key.startsWith(FEATURE_PREFIX)) continue;
    flags[entry.key] = toFlag(entry.value);
  }
  return flags;
}

function staticOverrides(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const features = getRuntimeConfig().features ?? {};
  for (const [k, v] of Object.entries(features)) out[featureKey(k)] = Boolean(v);
  return out;
}

let loadSequence = 0;

export const featureFlagsStore = create<FeatureFlagsState>()((set, get) => ({
  status: 'idle',
  flags: {},
  overrides: {},
  load: async (client = apiClient) => {
    const sequence = ++loadSequence;
    set({ status: 'loading' });
    try {
      const body = await client.get<unknown>(EFFECTIVE_CONFIGURATION_PATH, { query: { key: `${FEATURE_PREFIX}*` } });
      if (sequence !== loadSequence) return;
      set({ status: 'ready', flags: parseEffectiveFlags(body), overrides: { ...staticOverrides(), ...get().overrides } });
    } catch {
      if (sequence !== loadSequence) return;
      set({ status: 'error', overrides: { ...staticOverrides(), ...get().overrides } });
    }
  },
  setOverride: (key, value) => {
    const full = featureKey(key);
    const overrides = { ...get().overrides };
    if (value === null) delete overrides[full];
    else overrides[full] = value;
    set({ overrides });
  },
  isEnabled: (key) => {
    const full = featureKey(key);
    const { overrides, flags } = get();
    if (full in overrides) return overrides[full] === true;
    if (full in flags) return flags[full] === true;
    const runtime = getRuntimeConfig().features ?? {};
    return runtime[full] === true || runtime[key] === true;
  },
}));

/** Resets the store to its pristine state (tests). */
export function resetFeatureFlagsStore(): void {
  loadSequence += 1;
  featureFlagsStore.setState({ status: 'idle', flags: {}, overrides: {} });
}
